import { ChangeDetectionStrategy, Component, computed, OnInit, signal } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { ReadSectionEmptyComponent } from './readonly/read-section-empty.component';
import { readTitleSectionState, type ReadSectionState } from './readonly/read-section.util';

/** Figma Section "Label" (node 3266:130762): Lable/Medium/100 in Foreground/Default/Secondary,
 *  with 12px under it (the Section is a flex column with gap 12). `text-base` is 14px on this
 *  project's Tailwind scale, and `text-accent` resolves to #545559 light / #C7C7CC dark. */
const SECTION_HEADING_CLASS = 'text-base font-medium leading-none text-accent';

/** Figma Section gap: 12px between the label and the first field under it. */
const SECTION_HEADING_SPACING_CLASS = 'mb-3';

/** What every `title` field looked like before the Figma section-label styling landed. */
const INLINE_TITLE_CLASS = 'text-default font-bold';

@Component({
  selector: 'formly-field-title',
  imports: [InfoTooltipComponent, ReadSectionEmptyComponent],
  template: `<h3 [class]="renderedHeadingClass">
      {{ props.label !== '' ? props.label : formControl.value }}
      <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
    </h3>
    <!-- A flat section in the read view whose fields are all hidden (#21239): same empty state as a
         group section (group.component.ts), so the heading does not stand there blank. py-3, like a
         read row: the heading drops its own 12px in read mode (renderedHeadingClass). -->
    @if (isSectionHeading() && readState === 'empty') {
      <dd-read-section-empty />
    }`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyTitleComponent extends FieldType<any> implements OnInit {
  /**
   * Whether this title is a form SECTION heading — the only place the Figma section-label styling
   * (and its 12px of air) belongs.
   *
   * Deliberately opt-IN: 'title' is a public form-builder field type that is also used inline, and
   * restyling all of it would quietly restyle those too. The marker is the `w-full` that
   * form-generate.util.mjs puts on every field it generates, so this reads as "the form builder
   * laid this title out" — not "the title is full width" (a half-width title carries it too).
   * Hand-written usages have no `w-full` and keep their old look: the separator inside a flex row
   * (the 'tot' between two time fields in the availability block, 'flex-0 …'), the bare inline
   * value label (user-roles detail, className ''), and the admin headings that size themselves
   * through the wrapper.
   *
   * The check reads `field.className` rather than a CSS selector on the rendered <h3>: the class
   * list lives on the surrounding <formly-field>, not on the heading, so a selector scoped to this
   * component can never see it — which is exactly why the previous `h3.w-full` rule silently never
   * matched, and why no title ever got its 12px.
   */
  readonly isSectionHeading = signal(false);

  /**
   * Whether this heading supplies its own 12px. Separate from the typography on purpose: an author
   * who set `mb-1` on the field (the address block's 'Geolocatie' heading) wants a tighter gap, not
   * a different heading — folding the two together made that one heading render bold-black while
   * every sibling section label was grey.
   */
  readonly needsSectionSpacing = signal(false);

  readonly headingClass = computed(() => {
    if (!this.isSectionHeading()) {
      return INLINE_TITLE_CLASS;
    }
    return this.needsSectionSpacing() ? `${SECTION_HEADING_SPACING_CLASS} ${SECTION_HEADING_CLASS}` : SECTION_HEADING_CLASS;
  });

  /**
   * Read-view state of the fields this heading introduces (its following siblings up to the next
   * title). Evaluated per render: the siblings' `hide` flags are only resolved once Formly built them.
   */
  get readState(): Exclude<ReadSectionState, 'nested-empty'> {
    return this.field?.parent ? readTitleSectionState(this.field) : 'edit';
  }

  /**
   * The heading's classes as rendered. In the read view a section heading follows Figma's read card
   * ("Slot" gap 24, "Section" gap 12) instead of the edit spacing:
   * - 24px ABOVE, but only when something is drawn above it in the same card — the first heading
   *   sits on the card's own padding. Without it the heading sat 0px on the previous row's divider
   *   (measured on Gebruikers > Algemeen, 2026-09-15) and read as the tail of the section it closes;
   * - no margin BELOW: a read row carries 12px padding above its text, which already is Figma's 12.
   * A getter, not part of the `headingClass` computed: sibling `hide` flags are plain properties
   * Formly resolves after build, which a signal would never notice.
   */
  get renderedHeadingClass(): string {
    if (!this.isSectionHeading() || this.readState === 'edit') {
      return this.headingClass();
    }
    return this.#hasVisibleFieldAbove() ? `mt-6 ${SECTION_HEADING_CLASS}` : SECTION_HEADING_CLASS;
  }

  #hasVisibleFieldAbove(): boolean {
    const siblings = this.field.parent?.fieldGroup ?? [];
    const index = siblings.indexOf(this.field);
    return siblings.slice(0, Math.max(index, 0)).some(sibling => !sibling.hide);
  }

  ngOnInit(): void {
    const className = this.field?.className ?? '';
    this.isSectionHeading.set(/(^|\s)w-full(\s|$)/.test(className));
    // Any bottom margin the author set wins, responsive variants (`md:mb-2`) included.
    this.needsSectionSpacing.set(!/(^|\s)([a-z]+:)?mb-\S+(\s|$)/.test(className));
  }
}
